import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { HmsPush } from '@/services/hms-push.plugin';

export interface ClientDeviceMeta {
  deviceName: string;
  platform: string;
  appVersion?: string;
  appBuild?: string;
}

const DEVICE_NAME_MAX = 255;

/**
 * Метаданные клиента для сессии: платформа, версия приложения, имя устройства.
 */
export async function getClientDeviceMeta(): Promise<ClientDeviceMeta> {
  const deviceName = await resolveDeviceName();
  const platform = await resolvePlatform();
  const { appVersion, appBuild } = await resolveAppVersion();
  return { deviceName, platform, appVersion, appBuild };
}

async function resolvePlatform(): Promise<string> {
  try {
    if (Capacitor.isNativePlatform()) {
      const info = await Device.getInfo();
      if (info.platform === 'ios') return 'ios';
      if (info.platform === 'android') {
        try {
          const { provider } = await HmsPush.getPushProvider();
          if (provider === 'HMS') return 'huawei';
        } catch {
          // не HMS / плагин недоступен
        }
        return 'android';
      }
      if (info.platform) return String(info.platform).toLowerCase();
    }
  } catch {
    // fallback
  }
  return 'web';
}

async function resolveAppVersion(): Promise<{ appVersion?: string; appBuild?: string }> {
  try {
    const info = await App.getInfo();
    const appVersion = info.version?.trim() || undefined;
    const appBuild = info.build?.trim() || undefined;
    return { appVersion, appBuild };
  } catch {
    return {};
  }
}

async function resolveDeviceName(): Promise<string> {
  try {
    if (Capacitor.isNativePlatform()) {
      const info = await Device.getInfo();
      const parts: string[] = [];
      if (info.name?.trim()) parts.push(info.name.trim());
      else if (info.model?.trim()) parts.push(info.model.trim());
      if (info.platform === 'ios') {
        parts.push(info.osVersion ? `iOS ${info.osVersion}` : 'iOS');
      } else if (info.platform === 'android') {
        if (info.manufacturer?.trim() && info.model?.trim()) parts.push(`${info.manufacturer} ${info.model}`);
        else if (info.model?.trim()) parts.push(info.model);
        parts.push(info.osVersion ? `Android ${info.osVersion}` : 'Android');
      }
      const str = parts.length ? parts.join(', ') : 'Мобильное приложение';
      return str.length > DEVICE_NAME_MAX ? str.substring(0, DEVICE_NAME_MAX) : str;
    }
  } catch {
    // fallback
  }
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    const ua = navigator.userAgent;
    return ua.length > DEVICE_NAME_MAX ? ua.substring(0, DEVICE_NAME_MAX) : ua;
  }
  return 'Веб-браузер';
}
